import { ElementHelpers } from '../helpers/element-helpers';
import { breakpoints } from '../helpers/constants';

/**
 * Class to add support for a draggable component, such as for related articles
 */
export class Draggable {
	constructor(elem) {
		this.elem = elem;
		this.cards = this.elem.querySelectorAll('.card-holder__card');

		// Cancel if we have an empty draggable component
		if (!this.cards || this.cards.length === 0) return;

		this.slideElem = this.elem.querySelector('.draggable__track');
		this.prevButton = this.elem.querySelector('.draggable__control--backward');
		this.nextButton = this.elem.querySelector('.draggable__control--forward');

		this.current = 0;

		this.setCardWidth();
		this.cardsToShow = 3;
		this.slidePercentage = (this.cardWidth / this.slideElem.offsetWidth) * 100;

		this.toggleBreakPoints();

		this.transform = 0;
		this.toggleStyling();
		this.addEventHandlers();
	}

	/**
	 * Get the current index
	 * @returns {*}
	 */
	get current() {
		return parseInt(this._current);
	}

	/**
	 * Set the new current index
	 * @param currentValue The newly value
	 */
	set current(currentValue) {
		this.onPropertyChanged('current', currentValue, (value) => {
			this._current = value;
		});
	}

	/**
	 * Set the width of a card so we can calculate the slide percentage
	 */
	setCardWidth() {
		this.cardWidth = this.cards[0].offsetWidth + (parseInt(window.getComputedStyle(this.cards[0], null).getPropertyValue('margin-right')) * 2);
	}

	/**
	 * When our property 'current' is change, make sure that we set it appropriately.
	 * For example we can't have a 'current' lower than 0 or higher than the length of the cards array
	 * @param property
	 * @param val
	 * @param callback
	 */
	onPropertyChanged(property, val, callback) {
		let value;

		// Toggle our prevbutton
		if (val <= 0) {
			value = 0;
			this.prevButton.classList.add('draggable__control--hidden');
		} else {
			value = val;
			this.prevButton.classList.remove('draggable__control--hidden');
		}

		// Toggle our next button
		if (val >= (this.cards.length - 3)) {
			value = this.cards.length - 3;
			this.nextButton.classList.add('draggable__control--hidden');
		} else {
			value = val;
			this.nextButton.classList.remove('draggable__control--hidden');
		}

		// Execute passed callback
		if (typeof callback === 'function') {
			callback(value);
		}

		return value;
	}

	/**
	 * Add all necessary eventHandlers
	 */
	addEventHandlers() {
		this.prevButton.addEventListener('click', () => {
			if(this.current !== 0){
				this.onPrev();
			}
		});

		this.nextButton.addEventListener('click', () => {
			if(this.current !== this.cards.length - this.cardsToShow){
				this.onNext();
			}
		});

		window.addEventListener('resize', () => {
			this.onResize();
		});
	}

	/**
	 * On resize of our window we reset our draggable component to initial state
	 */
	onResize() {
		this.reset();
		this.slide();
		this.setCardWidth();
		this.toggleBreakPoints();
		this.toggleStyling();
	}

	/**
	 * Reset attributes in our object to initial state
	 */
	reset() {
		this.current = 0;
		this.transform = 0;
		this.cardsToShow = 3;
	}

	toggleBreakPoints() {
		this.cardsToShow = breakpoints.isMedium() ? 2 : 3;
	}

	/**
	 * Go to previous slide or stay at the first one
	 */
	onPrev() {
		this.current = this.current <= 0 ? 0 : this.current - 1;
		this.slide('prev');
		this.toggleStyling();
	}

	/**
	 * Go to the next slide or stay the the last one
	 */
	onNext() {
		this.current = this.current >= this.cards.length ? this.cards.length : this.current + 1;
		this.slide('next');
		this.toggleStyling();
	}

	/**
	 * Slide to the next, previous or first one
	 * @param direction
	 */
	slide(direction) {

		switch (direction) {
			case 'prev':
				this.transform = this.transform === 0 ? 0 : this.transform + this.slidePercentage;
				this.slideElem.style.transform = `translateX(${this.transform}%)`;
				break;
			case 'next':
				this.transform = this.transform === (this.cards.length * this.slidePercentage) ? this.transform : this.transform - this.slidePercentage;
				this.slideElem.style.transform = `translateX(${this.transform}%)`;
				break;
			default:
				this.slideElem.style.transform = `translateX(${this.transform})`;
		}
	}

	/**
	 * Toggle styling (such as opacity) for all cards
	 */
	toggleStyling() {
		for (let i = 0; i < this.cards.length; i++) {
			this.styleCard(this.cards[i], i);
		}
		this.slidePercentage = (this.cardWidth / this.slideElem.offsetWidth) * 100;
	}

	/**
	 * Style a single card by adding the appropriate class
	 * @param card
	 * @param position
	 */
	styleCard(card, position) {

		if (!ElementHelpers.isHtmlElement(card)) {
			return;
		}

		// Only show the first three without opacity
		if (position >= (this.current + this.cardsToShow)) {
			card.classList.add('card-holder__card--inactive');
		} else {
			card.classList.remove('card-holder__card--inactive');
		}

		// Always show the ones which are dragged with opacity
		if (position < this.current) {
			card.classList.add('card-holder__card--inactive');
		}
	}

	/**
	 * Get our component selector
	 * @returns {string}
	 */
	static getSelector() {
		return '[data-draggable]';
	}
}

